import { useEffect } from 'react';
import { Layer, type MapMouseEvent, Source, useMap } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useTheme } from 'styled-components';

import { SHAPE_FILL_OPACITY } from '../../../constants';
import { useMultiFlightMap } from '../../../MultiFlightMap.context';
import { getZoneColor, getZoneFillColor, getZoneFillOpacity } from '../../../MultiFlightMap.helpers';

import type { Feature, FeatureCollection } from 'geojson';
import type { MapShape } from '../../MapView/map.types';

type Props = {
  mapId: string;
};

function buildShapeFeature(shape: MapShape, uiColors: any): Feature {
  const properties = {
    color: getZoneColor(uiColors, shape),
    fillColor: getZoneFillColor(uiColors, shape),
    fillOpacity: getZoneFillOpacity(shape, SHAPE_FILL_OPACITY),
    id: shape.id
  };

  return shape.type === 'MultiPolygon'
    ? { geometry: { coordinates: shape.coordinates, type: 'MultiPolygon' }, properties, type: 'Feature' }
    : { geometry: { coordinates: shape.coordinates, type: 'Polygon' }, properties, type: 'Feature' };
}

export function PolygonShapeLayer({ mapId }: Props) {
  const { shapes } = useMultiFlightMap();
  const { uiColors }: any = useTheme();
  const { [mapId]: map } = useMap();
  const { editedPolygonId, setEditedPolygonId, setList, setSelectedItemId } = useMultiFlightMap();

  useEffect(() => {
    const mapInstance = map?.getMap();

    if (!mapInstance) {
      return;
    }

    const handleClick = (e: MapMouseEvent) => {
      const feature = e.features?.[0];
      const id = feature?.properties?.id;
      setEditedPolygonId(id);
      setSelectedItemId(id.toString() || null);
      setList('locations');
    };

    mapInstance.on('click', 'fill-layer', handleClick);

    return () => {
      mapInstance.off('click', 'fill-layer', handleClick);
    };
  }, [editedPolygonId, map, setEditedPolygonId]);

  if (shapes.length === 0) {
    return null;
  }

  const featureCollection: FeatureCollection = {
    features: shapes.filter(shape => shape.id !== editedPolygonId).map(shape => buildShapeFeature(shape, uiColors)),
    type: 'FeatureCollection'
  };

  return (
    <Source data={featureCollection} id="shapes" type="geojson">
      <Layer
        id="fill-layer"
        paint={{
          'fill-color': ['get', 'fillColor'],
          'fill-opacity': ['get', 'fillOpacity']
        }}
        type="fill"
      />
      <Layer
        id="line-layer"
        paint={{
          'line-color': ['get', 'color'],
          'line-width': 2
        }}
        type="line"
      />
    </Source>
  );
}
